import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useCallback, useState } from 'react';
import { useDeepCompareMemo } from 'use-deep-compare';
import { Block } from '../../../../services/entities/impl/blfApi/schemas/block';
import { usePointHeatmap } from '../../../commonUI/maps/hooks/usePointHeatmap';
import { SINGLE_POINT_LAYER_ID } from '../../../commonUI/maps/layers/constants';
import { PointHeatmap } from '../../../commonUI/maps/PointHeatmap';
import {
  PointColorConfiguration,
  PointFeatureProperties,
} from '../../../commonUI/maps/types';
import { CollectionPoint } from '../../types';
import { CollectionPointDetailsControl } from './controls/CollectionPointDetailsControl';
import { EXPOSURE_COLOR_CONFIG } from './controls/PointColorizerControl/colorConfigurations';
import { PointColorizerControl } from './controls/PointColorizerControl/PointColorizerControl';
import { PointDetailsTable } from './controls/PointDetailsTable/PointDetailsTable';

type Props = {
  points: CollectionPoint[];
  blocks: Block[];
  hiddenBlockIds?: Block['id'][];
};

export const CollectionPointsMap = ({
  points,
  blocks,
  hiddenBlockIds = [],
}: Props) => {
  const [selectedPoint, setSelectedPoint] =
    useState<PointFeatureProperties<CollectionPoint>>();
  const [pointColorConfig, setPointColorConfig] = useState<
    PointColorConfiguration<CollectionPoint>
  >(EXPOSURE_COLOR_CONFIG);

  const isSelectedPointHidden = selectedPoint
    ? hiddenBlockIds.includes(selectedPoint?.blockId)
    : true;

  const filteredPoints = useDeepCompareMemo(
    () => points.filter((pt) => !hiddenBlockIds.includes(pt.blockId)),
    [points, hiddenBlockIds]
  );
  const filteredBlocks = useDeepCompareMemo(
    () => blocks.filter((block) => !hiddenBlockIds.includes(block.id)),
    [blocks, hiddenBlockIds]
  );

  const { pointsFeatureCollection, blocksFeatureCollection } = usePointHeatmap(
    filteredPoints,
    pointColorConfig,
    filteredBlocks
  );

  /**
   * PointDetailsViewer action handlers
   */
  const handleDataDisplayClose = useCallback(() => {
    setSelectedPoint(undefined);
  }, []);

  const handleNextPoint = useCallback(() => {
    const pointFeatures = pointsFeatureCollection.features;
    setSelectedPoint((prevSelectedPoint) => {
      if (prevSelectedPoint) {
        const indexOfNewPoint =
          prevSelectedPoint?.index === pointFeatures.length - 1
            ? 0
            : prevSelectedPoint.index + 1;
        return pointFeatures[indexOfNewPoint].properties;
      }
    });
  }, [pointsFeatureCollection.features]);

  const handlePreviousPoint = useCallback(() => {
    const pointFeatures = pointsFeatureCollection.features;
    setSelectedPoint((prevSelectedPoint) => {
      if (prevSelectedPoint) {
        const indexOfNewPoint =
          prevSelectedPoint?.index === 0
            ? pointFeatures.length - 1
            : prevSelectedPoint.index - 1;
        return pointFeatures[indexOfNewPoint].properties;
      }
    });
  }, [pointsFeatureCollection.features]);

  /**
   * Map Layer Event Handlers
   */
  const handleMapLayerClick = useCallback(
    (event: mapboxgl.MapLayerMouseEvent) => {
      if (event.features && event.features.length > 0) {
        const { layer, properties } = event.features[0];
        switch (layer.id) {
          case SINGLE_POINT_LAYER_ID:
            setSelectedPoint(
              properties as PointFeatureProperties<CollectionPoint> | undefined
            );
            break;
          default:
            break;
        }
      }
    },
    []
  );

  return (
    <PointHeatmap<CollectionPoint>
      pointsFeatureCollection={pointsFeatureCollection}
      blocksFeatureCollection={blocksFeatureCollection}
      pointColorConfig={pointColorConfig}
      selectedPointId={selectedPoint?.id}
      onMapClick={handleMapLayerClick}
      hideColorLegend
    >
      {/* Map Controls */}
      <PointColorizerControl
        colorConfig={pointColorConfig}
        onChange={setPointColorConfig}
      />
      {selectedPoint && (
        <CollectionPointDetailsControl
          point={selectedPoint}
          onNextPoint={handleNextPoint}
          onPreviousPoint={handlePreviousPoint}
          open={!isSelectedPointHidden}
          onClose={handleDataDisplayClose}
        >
          <PointDetailsTable details={selectedPoint} />
        </CollectionPointDetailsControl>
      )}
    </PointHeatmap>
  );
};
